import React, { CSSProperties, Suspense, useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Dimmer, Grid, Header, Icon, Image, List, Loader, Popup } from 'semantic-ui-react'

// Note: React warnings about defaultProps and findDOMNode are expected from Semantic UI React library
// These are library-level issues that will be resolved when Semantic UI React is updated for React 18+
import admin from '../../../assets/svg/admin.svg'
import help from '../../../assets/svg/help.svg'
import collapsed from '../../../assets/svg/menu_icon_collapsed.svg'
import expanded from '../../../assets/svg/menu_icon_exp.svg'
import photo from '../../../assets/svg/photo.svg'
import account from '../../../assets/svg/profile.svg'
import sign_out from '../../../assets/svg/sign_out.svg'
import trialytix from '../../../assets/svg/trialytix.svg'
import { IMenuItem } from '../../../types/menuItems'
import { initalUsersState, IUserDetail } from '../../../types/user'
import { checkRole } from '../../../utils/checkRole'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'
import NotificationsSidebarContainer from '../../notifications/containers/NotificationsSidebarContainer'
import { INotificationData } from '../../notifications/types/notification'
import { IProject } from '../../project/types/project'
import { globalMenu } from '../constants/menuItems'
const ConfirmCloseModal = React.lazy(() => import('../../../components/Modal/ConfirmDeleteModal'))

/**
 * Check if restricted mode should show all menu items
 * When true, shows all menu items even if user lacks rights (with contact page redirect)
 */
const isRestrictedShowAllItems = (): boolean => {
  return import.meta.env.VITE_ENABLE_RESTRICTED_SHOW_ALL_ITEMS === 'true'
}

interface Props {
  sidebarVisible: boolean
  activeMenuItem: IMenuItem
  selectedProject: IProject
  formOpenAndDirty: boolean
  openModal: boolean
  user: IUserDetail | null
  notifications: INotificationData[]
  showSidebar: () => void
  setActiveMenuItem: (item: string, path: string) => void
  logout: () => void
  setFormOpen: (open: boolean) => void
  setFormDirty: (dirty: boolean) => void
  setOpenConfirmModal: (open: boolean) => void
  getNotifications: () => void
}

const _listItemStyle = {
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRadius: '0.28rem'
}
const _imageStyle = { marginRight: '1rem', marginLeft: '0.5rem' }

const settingsMenu = [
  { name: 'Profile', key: 'profile', image: account, rights: [] },
  { name: 'Admin', key: 'admin', image: admin, rights: [1, 2, 3, 4, 5] },
  { name: 'Help Center', key: 'help', image: help, rights: [], disabled: false },
  { name: 'Sign Out', key: 'sign_out', image: sign_out, rights: [] },
]

const SidebarIcons = (props: Props): JSX.Element => {
  const {
    sidebarVisible,
    activeMenuItem,
    user,
    selectedProject,
    formOpenAndDirty,
    openModal,
    notifications,
    setFormOpen,
    setOpenConfirmModal,
    setFormDirty,
    showSidebar,
    setActiveMenuItem,
    getNotifications,
    logout,
  } = props

  const [hoveredItem, setHoveredItem] = useState('')
  const [redirectPath, setRedirectPath] = useState({ item: '', path: '' })
  const [renderModal, setRenderModal] = useState(false)
  const [_notifications, setNotifications] = useState(notifications)
  const _history = useHistory()
  const _location = useLocation()
  const { featureFlags } = useFeatureFlags()

  /**
   * Checks if notifications feature is restricted
   * @returns true if notifications should redirect to contact sales
   */
  const isNotificationsRestricted = (): boolean => {
    if (!featureFlags || !featureFlags.enable_restricted_access_mode) return false
    return featureFlags.restricted_features.notifications === true
  }

  /**
   * Checks if we should show all menu items in restricted mode
   * @returns true if restricted mode is on AND show all items flag is enabled
   */
  const shouldShowAllMenuItems = (): boolean => {
    if (!featureFlags || !featureFlags.enable_restricted_access_mode) return false
    return isRestrictedShowAllItems()
  }

  useEffect(() => {
    setNotifications(notifications)
  }, [notifications])

  useEffect(() => {
    if (user && user['username'] !== 'boss-user') {
      const intervalTime = 55000 + Math.floor(Math.random() * 10000)
      const timer = setInterval(() => getNotifications(), intervalTime)
      return () => clearInterval(timer)
    }
  }, [user, getNotifications])

  const barButtonStyle = () => {
    const baseStyle = {
      position: 'absolute',
      transition: 'all 0.50s ease',
      zIndex: 1,
      width: '2.5rem'
    }
    if (sidebarVisible) {
      return { ...baseStyle, left: '286px' }
    }
    return { ...baseStyle, left: '66px' }
  }

  const handleItemClicked = () => {
    showSidebar()
  }

  const handleMenuItemClicked = (e: MouseEvent, item: string, path: string, menuItem?: IMenuItem) => {
    if (item === 'notification') {
      if (isNotificationsRestricted()) {
        setActiveMenuItem(item, '/contact-sales')
        _history.push('/contact-sales')
      } else if (activeMenuItem.name === 'notification') {
        setActiveMenuItem('notification', '/before-close-notification')
      } else {
        setActiveMenuItem(item, _location.pathname)
      }
    } else {
      if (formOpenAndDirty) {
        e.preventDefault()
        setOpenConfirmModal(true)
        setRenderModal(true)
        setRedirectPath({ item: item, path: path })
      } else {
        setActiveMenuItem(item, path)
        _history.push(path)
      }
    }
  }

  const onMouseEnter = (item: string) => {
    setHoveredItem(item)
  }

  const onMouseLeave = () => {
    setHoveredItem('')
  }

  const handleSettingsClicked = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, key: string) => {
    if (key === 'help') {
      e.preventDefault()
      window.open('https://trialytix.notion.site/trialytix-academy', '_blank')
    } else if (formOpenAndDirty) {
      e.preventDefault()
      setOpenConfirmModal(true)
      setRenderModal(true)
      setRedirectPath({ item: key, path: `/${key}` })
    } else {
      if (key === 'sign_out') {
        logout()
      } else {
        setActiveMenuItem(key, `/${key}`)
      }
    }
  }

  const renderToggleButton = () => {
    const _locArr = _location.pathname.split('/').filter((path) => path !== '')
    const id = _locArr.length > 2 ? _locArr[1] : ''
    if (
      Object.prototype.hasOwnProperty.call(selectedProject, 'id') &&
      _locArr.length > 2 &&
      _locArr.includes('projects') &&
      +selectedProject.id === +id &&
      selectedProject.id &&
      _history.location.pathname !== `/accounting/${selectedProject.id}` &&
      _history.location.pathname !== '/accounting' &&
      activeMenuItem.name !== 'notification'
    ) {
      return (
        <Image
          size='small'
          src={sidebarVisible ? expanded : collapsed}
          style={barButtonStyle()}
          onClick={handleItemClicked}
        />
      )
    }
    return null
  }

  const confirmClose = (action: 'cancel' | 'delete') => {
    if (action === 'delete') {
      setFormOpen(false)
      setFormDirty(false)
      setActiveMenuItem(redirectPath.item, redirectPath.path)
      _history.push(redirectPath.path)
      if (redirectPath.item === 'sign_out') {
        logout()
      }
    }
    setOpenConfirmModal(false)
    setRenderModal(false)
  }

  return (
    <Grid style={{ width: '88px', margin: 0 }}>
      <Grid.Column color='purple' style={{ padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column' }}>
        <div style={{ marginTop: '22px', display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', flexDirection: 'column' }}>
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
            <Image
              size='small'
              src={trialytix}
              style={{ position: 'relative', height: '38px' }}
            />
            {renderToggleButton()}
          </div>
          {globalMenu.map((i) => {
            const _name = i.name.toLocaleLowerCase().split(' ').join('-')
            const _src =
              _name === hoveredItem && _name !== activeMenuItem.name
                ? i.hoverImg
                : _name === activeMenuItem.name
                  ? i.activeImg
                  : i.normalImg
            // Check if user has access or if we should show all items in restricted mode
            const hasAccess = user && checkRole(user, i.rights)
            const showAllItems = shouldShowAllMenuItems()

            const selectedStyle: CSSProperties =
              activeMenuItem.name === _name || hoveredItem === _name
                ? { borderRadius: '10px', background: 'rgba(8,214,161,0.1)', transform: 'scale(1.1)', transition: 'transform 0.2s ease' }
                : { transition: 'transform 0.2s ease' }

            // Show item if user has access OR if showing all items in restricted mode
            if (hasAccess || showAllItems) {
              if (_name === 'notification') {
                const notificationsRestricted = isNotificationsRestricted()
                const notificationSidebarStyle: CSSProperties =
                  activeMenuItem.name === 'notification' && !notificationsRestricted
                    ? { paddingRight: 0, background: 'transparent', border: 'none', boxShadow: 'none' }
                    : {}
                return (
                  <Popup
                    key={_name}
                    className={activeMenuItem.name === 'notification' && !notificationsRestricted ? 'notification-sidebar-popup' : 'popup-info'}
                    basic={activeMenuItem.name === 'notification' && !notificationsRestricted ? true : undefined}
                    open={
                      (activeMenuItem.name === 'notification' && !notificationsRestricted) || (_name === hoveredItem && _name !== activeMenuItem.name)
                    }
                    offset={[-5, 0]}
                    style={notificationSidebarStyle}
                    trigger={
                      <Icon.Group>
                        <Image
                          onMouseEnter={() => onMouseEnter(_name)}
                          onMouseLeave={() => onMouseLeave()}
                          style={{
                            width: '4rem',
                            height: '4rem',
                            marginBottom: '.4rem',
                            marginTop: '.4rem',
                            display: 'flex',
                            justifyContent: 'center',
                            cursor: i.disabled ? 'no-drop' : 'pointer',
                            ...selectedStyle,
                          }}
                          src={_src}
                          onClick={(e: MouseEvent) => (i.disabled ? null : handleMenuItemClicked(e, _name, i.path, i))}
                        />
                        {!notificationsRestricted && _notifications.filter((n) => !n.seen).length > 0 && (
                          <Icon
                            name='circle'
                            color='red'
                            style={{
                              marginLeft: activeMenuItem.name === _name || hoveredItem === _name ? '1.4rem' : '1.2rem',
                              marginTop: activeMenuItem.name === _name || hoveredItem === _name ? '1rem' : '.8rem',
                              fontSize: '.6rem',
                            }}
                          />
                        )}
                      </Icon.Group>
                    }
                    position='right center'
                    content={activeMenuItem.name === 'notification' && !notificationsRestricted ? <NotificationsSidebarContainer /> : i.name}
                  />
                )
              } else if (_name === 'user-dashboard') {
                // Only show user-dashboard if user doesn't have global dashboard access
                // Don't show when showAllItems is true (Dashboard is already visible)
                if (!showAllItems && !checkRole(user!, [27])) {
                  return (
                    <Popup
                      key={_name}
                      className='popup-info'
                      offset={[-5, 0]}
                      trigger={
                        <Image
                          onMouseEnter={() => onMouseEnter(_name)}
                          onMouseLeave={() => onMouseLeave()}
                          style={{
                            width: '4rem',
                            height: '4rem',
                            marginTop: '.4rem',
                            marginBottom: '.4rem',
                            display: 'flex',
                            justifyContent: 'center',
                            cursor: i.disabled ? 'no-drop' : 'pointer',
                            ...selectedStyle,
                          }}
                          src={_src}
                          onClick={(e: MouseEvent) => (i.disabled ? null : handleMenuItemClicked(e, _name, i.path, i))}
                        />
                      }
                      position='right center'
                      content={i.name}
                    />
                  )
                }
              } else if (_name !== 'settings' && _name !== 'user-dashboard') {
                return (
                  <Popup
                    key={_name}
                    className='popup-info'
                    offset={[-5, 0]}
                    trigger={
                      <Image
                        onMouseEnter={() => onMouseEnter(_name)}
                        onMouseLeave={() => onMouseLeave()}
                        style={{
                          width: '4rem',
                          height: '4rem',
                          marginTop: '.4rem',
                          marginBottom: '.4rem',
                          display: 'flex',
                          justifyContent: 'center',
                          cursor: i.disabled ? 'no-drop' : 'pointer',
                          ...selectedStyle,
                        }}
                        src={_src}
                        onClick={(e: MouseEvent) => (i.disabled ? null : handleMenuItemClicked(e, _name, i.path, i))}
                      />
                    }
                    position='right center'
                    content={i.name}
                  />
                )
              }
            }
            return null
          })}
        </div>
        <Popup
          basic={true}
          on='click'
          hoverable={true}
          style={{ width: '15.2rem' }}
          trigger={
            <Image src={photo} style={{ cursor: 'pointer', marginBottom: '1.5rem', width: '2.5rem', height: '3rem' }} />
          }
          position='top center'
          offset={[130, -50]}
        >
          <List>
            <List.Item>
              <Header
                style={{ fontSize: '1.5rem', color: '#232C58', marginLeft: '0.5rem', marginBottom: '.5rem' }}
                size='huge'
              >
                Settings
              </Header>
            </List.Item>
            {settingsMenu.map((item, idx) => {
              if (user && checkRole(user, item.rights)) {
                return (
                  <List.Item
                    as={item.disabled ? 'div' : Link}
                    to={`/${item.key === 'admin' ? item.key + '/user' : item.key}`}
                    key={item.key}
                    onClick={(e) => (item.disabled ? null : handleSettingsClicked(e, item.key))}
                    className='settings-menu'
                    style={{ ..._listItemStyle, marginBottom: idx === settingsMenu.length - 1 ? '' : '1rem' }}
                  >
                    <Image src={item.image} style={_imageStyle} />
                    <div
                      style={{ fontSize: '1.28rem', color: idx === settingsMenu.length - 1 ? '#FA5953' : '#172533' }}
                    >
                      {item.name}
                    </div>
                  </List.Item>
                )
              } else {
                return null
              }
            })}
          </List>
        </Popup>
      </Grid.Column>
      <Suspense
        fallback={
          <Dimmer active inverted>
            <Loader size='big' />
          </Dimmer>
        }
      >
        <ConfirmCloseModal open={openModal && renderModal} closeModal={true} userInput={confirmClose} />
      </Suspense>
    </Grid>
  )
}

export default SidebarIcons
